import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useToast } from "@/components/ui/use-toast";
import { useData } from "@/hooks/useData";
import { Company } from "@/types/company/company.types";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigation } from "@/contexts/NavigationContext";
import { CompanyInfo } from "@/components/dashboard/CompanyInfo";
import { useMemo } from "react";
import { FootprintDocument, FootprintProduct } from "@/types/results";
import { ArrowLeft, Droplets, Leaf, Calculator, Activity } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { formatNumberDynamic } from "@/utils/numberFormatter";

export const CompanyDashboard = () => {
  const { claims } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const { goBack } = useNavigation();
  const navigate = useNavigate();
  const { id: companyId } = useParams();

  const {
    document: company,
    loading,
    error,
  } = useData<Company>({
    collectionName: "companies",
    documentId: claims?.role === "company" ? claims?.companyId : companyId,
  });

  // Fetch real data from the footprints collections
  const { data: footprintDocuments, loading: footprintsLoading } = useData<FootprintDocument>({
    collectionName: "footprints",
  });

  const { data: footprintProducts, loading: productsLoading } = useData<FootprintProduct>({
    collectionName: "footprint_products",
  });

  // Filter and organize data for current company
  const companyData = useMemo(() => {
    if (!company?.id) {
      return { footprints: [], products: [] };
    }

    // Filter footprint documents for the current company
    const companyFootprints = footprintDocuments?.filter((fp) => fp.company_id === company.id) || [];

    // Get all footprint IDs for this company
    const companyFootprintIds = companyFootprints.map((fp) => fp.id);

    // Filter products that belong to this company's footprints
    const companyProducts = footprintProducts?.filter((fp) => companyFootprintIds.includes(fp.footprint_id)) || [];

    return {
      footprints: companyFootprints,
      products: companyProducts,
    };
  }, [company?.id, footprintDocuments, footprintProducts]);

  // Transform data for dashboard display
  const products = useMemo(() => {
    return companyData.products.map((product) => {
      const footprintDoc = companyData.footprints.find((fp) => fp.id === product.footprint_id);

      return {
        id: product.id,
        name: product.name || "Unknown Product",
        waterFootprint: (product.hydric_footprint_total || 0) * (product.annual_production || 0), // Annual total
        carbonFootprint: (product.carbon_footprint_total || 0) * (product.annual_production || 0), // Annual total
        calculatedDate: footprintDoc?.year.toString() || new Date().getFullYear().toString(),
        productType: product.product_type || "outro",
        unit: product.functional_unit?.unit || "m³",
        activityType: product.product_flow || "extraction", // Use product-specific flow instead of company activity type
      };
    });
  }, [companyData]);

  // Calculate totals
  const totalWaterFootprint = products.reduce((sum, product) => sum + product.waterFootprint, 0);
  const totalCarbonFootprint = products.reduce((sum, product) => sum + product.carbonFootprint, 0);

  // Data for carbon footprint chart
  const carbonChartData = products.map((product) => ({
    name: product.name.length > 15 ? product.name.substring(0, 15) + "..." : product.name,
    carbon: product.carbonFootprint,
    fullName: product.name,
  }));

  // Data for water footprint chart
  const waterChartData = products.map((product) => ({
    name: product.name.length > 15 ? product.name.substring(0, 15) + "..." : product.name,
    water: product.waterFootprint, // Convert to thousands for readability
    fullName: product.name,
  }));

  // Handle product click (if needed for navigation)
  // const handleProductClick = (productId: string) => {
  //   navigate(`/results?product=${productId}`);
  // };

  const handleEdit = () => {
    if (company?.id) {
      navigate(`/companies/${company.id}/edit`);
    }
  };

  if (loading || footprintsLoading || productsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    toast({
      variant: "destructive",
      title: t("error"),
      description: t("dashboard.errors.fetchFailed"),
    });
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-700">{t("dashboard.errors.companyNotFound")}</h2>
          <p className="text-gray-500">{t("dashboard.errors.noCompanyId")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section with Back Button for Assimagra users */}
      {(claims?.role === "assimagra" || claims?.role === "get2c") && (
        <Button variant="ghost" onClick={goBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("navigation.back")}
        </Button>
      )}

      {/* Welcome Section */}
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">{company.name}</h1>
        <p className="text-muted-foreground">{t("dashboard.company.welcome")}</p>
      </div>

      {/* Company Information Section */}
      <div>
        <CompanyInfo company={company} onEdit={handleEdit} claims={claims} />
      </div>

      {/* Summary Cards - Total Values */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("dashboard.company.totalProducts")}</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products.length}</div>
            <p className="text-xs text-muted-foreground">{t("dashboard.company.productsCalculated")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("dashboard.company.totalWaterFootprint")}</CardTitle>
            <Droplets className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalWaterFootprint.toFixed(1)} {t("dashboard.company.cubicM")}
            </div>
            <p className="text-xs text-muted-foreground">{t("dashboard.company.totalCalculatedProducts")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("dashboard.company.totalCarbonFootprint")}</CardTitle>
            <Leaf className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalCarbonFootprint.toFixed(1)} {t("dashboard.company.kgCO2")}
            </div>
            <p className="text-xs text-muted-foreground">{t("dashboard.company.totalCalculatedProducts")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Products List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            {t("dashboard.company.calculatedProducts")}
          </CardTitle>
          <CardDescription>{t("dashboard.company.productsList")}</CardDescription>
        </CardHeader>
        <CardContent>
          {products.length === 0 ? (
            <div className="text-center py-8">
              <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t("dashboard.company.noProductsCalculated")}</h3>
              <p className="text-gray-500 mb-6">{t("dashboard.company.calculateFirstProduct")}</p>
              <Button onClick={() => navigate("/footprints")} className="bg-teal-600 hover:bg-teal-700 text-white">
                <Calculator className="mr-2 h-4 w-4" />
                {t("dashboard.company.calculateFirstFootprint")}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-lg">{product.name}</h4>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {product.productType}
                      </span>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {t(`activityTypes.${product.activityType}`)}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {product.calculatedDate}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-blue-600 font-bold text-lg">
                        {product.waterFootprint.toFixed(2)} {t("dashboard.company.cubicM")}
                      </p>
                      <p className="text-gray-500">{t("dashboard.company.waterFootprint")}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-green-600 font-bold text-lg">
                        {product.carbonFootprint.toFixed(1)} {t("dashboard.company.kgCO2")}
                      </p>
                      <p className="text-gray-500">{t("dashboard.company.carbonFootprint")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Section - Two separate charts */}
      {products.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Carbon Footprint Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Leaf className="h-5 w-5" />
                {t("dashboard.company.carbonFootprintChart")}
              </CardTitle>
              <CardDescription>{t("dashboard.company.carbonFootprintAnnualTotals")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={carbonChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      label={{
                        value: "kg CO₂e",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      formatter={(value) => [
                        `${formatNumberDynamic(Number(value))} kg CO₂e`,
                        t("dashboard.company.carbonFootprint"),
                      ]}
                      labelFormatter={(label) => {
                        const item = carbonChartData.find((d) => d.name === label);
                        return item ? item.fullName : label;
                      }}
                    />
                    <Bar dataKey="carbon" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Water Footprint Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Droplets className="h-5 w-5" />
                {t("dashboard.company.waterFootprintChart")}
              </CardTitle>
              <CardDescription>{t("dashboard.company.waterFootprintAnnualTotals")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={waterChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      label={{
                        value: "m³",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      formatter={(value) => [
                        `${formatNumberDynamic(Number(value))} m³`,
                        t("dashboard.company.waterFootprint"),
                      ]}
                      labelFormatter={(label) => {
                        const item = waterChartData.find((d) => d.name === label);
                        return item ? item.fullName : label;
                      }}
                    />
                    <Bar dataKey="water" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
